'use client';

/**
 * OfflineIndicator Component
 *
 * Displays a banner or snackbar when the user is offline.
 * Designed for PWA offline support with automatic dismissal on reconnection.
 */

import CloudOffIcon from '@mui/icons-material/CloudOff';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';
import Snackbar from '@mui/material/Snackbar';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { forwardRef, useEffect, useState } from 'react';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import { glassEffects } from '../../theme/shadows';
import { motionPresets, transitionPresets } from '../../theme/transitions';
import { transientOptions } from '../../utils/styledUtils';

// ============================================================================
// Types
// ============================================================================

export type OfflineIndicatorVariant = 'banner' | 'snackbar' | 'minimal';
export type OfflineIndicatorPosition = 'top' | 'bottom';

export interface OfflineIndicatorProps {
  /**
   * Display variant
   * - banner: Full-width banner at top/bottom
   * - snackbar: MUI Snackbar at bottom
   * - minimal: Small floating indicator
   * @default 'banner'
   */
  variant?: OfflineIndicatorVariant;

  /**
   * Position for banner variant
   * @default 'top'
   */
  position?: OfflineIndicatorPosition;

  /**
   * Custom message to display when offline
   * @default 'You are currently offline'
   */
  message?: string;

  /**
   * Show reconnected message briefly when coming back online
   * @default true
   */
  showReconnected?: boolean;

  /**
   * Duration to show reconnected message (ms)
   * @default 3000
   */
  reconnectedDuration?: number;

  /**
   * Auto-hide the indicator after showing (for snackbar variant)
   * @default false for banner, true for snackbar
   */
  autoHide?: boolean;

  /**
   * Auto-hide duration (ms)
   * @default 6000
   */
  autoHideDuration?: number;

  /**
   * Callback when offline status changes
   */
  onStatusChange?: (isOnline: boolean) => void;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledBannerProps {
  $isDark: boolean;
  $position: OfflineIndicatorPosition;
}

const StyledBanner = styled(
  Box,
  transientOptions,
)<StyledBannerProps>(({ theme, $isDark, $position }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    position: 'fixed',
    left: 0,
    right: 0,
    zIndex: theme.zIndex.snackbar + 1,
    ...($position === 'top' ? { top: 0 } : { bottom: 0 }),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 2),
    background: $isDark ? 'rgba(211, 47, 47, 0.9)' : 'rgba(211, 47, 47, 0.95)',
    backdropFilter: glass.backdropBlur,
    transition: transitionPresets.card,
    boxShadow: $position === 'top' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 -2px 8px rgba(0, 0, 0, 0.3)',
  };
});

const ReconnectedBanner = styled(
  Box,
  transientOptions,
)<StyledBannerProps>(({ theme, $isDark, $position }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    position: 'fixed',
    left: 0,
    right: 0,
    zIndex: theme.zIndex.snackbar + 1,
    ...($position === 'top' ? { top: 0 } : { bottom: 0 }),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 2),
    background: $isDark ? 'rgba(46, 125, 50, 0.9)' : 'rgba(46, 125, 50, 0.95)',
    backdropFilter: glass.backdropBlur,
    transition: transitionPresets.card,
    boxShadow: $position === 'top' ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 -2px 8px rgba(0, 0, 0, 0.3)',
  };
});

const MinimalIndicator = styled(
  Box,
  transientOptions,
)<{ $isDark: boolean }>(({ theme, $isDark }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    position: 'fixed',
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    zIndex: theme.zIndex.snackbar,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: 20,
    background: $isDark ? 'rgba(211, 47, 47, 0.9)' : 'rgba(211, 47, 47, 0.95)',
    backdropFilter: glass.backdropBlur,
    boxShadow: glass.shadow,
  };
});

const MotionBanner = motion.create(StyledBanner);
const MotionReconnected = motion.create(ReconnectedBanner);
const MotionMinimal = motion.create(MinimalIndicator);

// ============================================================================
// Component
// ============================================================================

export const OfflineIndicator = forwardRef<HTMLDivElement, OfflineIndicatorProps>(
  (
    {
      variant = 'banner',
      position = 'top',
      message = 'You are currently offline',
      showReconnected = true,
      reconnectedDuration = 3000,
      autoHide,
      autoHideDuration = 6000,
      onStatusChange,
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const { isOnline, wasOffline } = useNetworkStatus();

    const [showReconnectedBanner, setShowReconnectedBanner] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // Handle status changes
    useEffect(() => {
      onStatusChange?.(isOnline);
    }, [isOnline, onStatusChange]);

    // Handle reconnection message
    useEffect(() => {
      if (isOnline && wasOffline && showReconnected) {
        setShowReconnectedBanner(true);
        const timer = setTimeout(() => {
          setShowReconnectedBanner(false);
        }, reconnectedDuration);
        return () => clearTimeout(timer);
      }
      return undefined;
    }, [isOnline, wasOffline, showReconnected, reconnectedDuration]);

    // Handle snackbar open state
    useEffect(() => {
      if (!isOnline && variant === 'snackbar') {
        setSnackbarOpen(true);
      }
    }, [isOnline, variant]);

    const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') return;
      setSnackbarOpen(false);
    };

    const shouldAutoHide = autoHide ?? variant === 'snackbar';

    // Snackbar variant
    if (variant === 'snackbar') {
      return (
        <>
          <Snackbar
            ref={ref}
            open={!isOnline && snackbarOpen}
            autoHideDuration={shouldAutoHide ? autoHideDuration : null}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            message={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WifiOffIcon fontSize="small" />
                <Typography variant="body2">{message}</Typography>
              </Box>
            }
            slots={{
              transition: Slide,
            }}
          />
          {showReconnected && (
            <Snackbar
              open={showReconnectedBanner}
              autoHideDuration={reconnectedDuration}
              onClose={() => setShowReconnectedBanner(false)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              message={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CloudOffIcon fontSize="small" sx={{ transform: 'scaleX(-1)' }} />
                  <Typography variant="body2">Back online</Typography>
                </Box>
              }
              slots={{
                transition: Slide,
              }}
              slotProps={{
                content: {
                  sx: {
                    backgroundColor: 'success.main',
                  },
                },
              }}
            />
          )}
        </>
      );
    }

    // Minimal variant
    if (variant === 'minimal') {
      return (
        <AnimatePresence mode="wait">
          {!isOnline && (
            <MotionMinimal
              ref={ref}
              $isDark={isDark}
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ duration: 0.2 }}
            >
              <WifiOffIcon fontSize="small" sx={{ color: 'white' }} />
              <Typography variant="caption" sx={{ color: 'white', fontWeight: 500 }}>
                Offline
              </Typography>
            </MotionMinimal>
          )}
        </AnimatePresence>
      );
    }

    // Banner variant (default)
    const slideProps = position === 'top' ? { y: -100 } : { y: 100 };

    return (
      <>
        <AnimatePresence mode="wait">
          {!isOnline && (
            <MotionBanner
              ref={ref}
              $isDark={isDark}
              $position={position}
              initial={{ ...slideProps, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ ...slideProps, opacity: 0 }}
              transition={motionPresets.fadeInUp.transition}
            >
              <WifiOffIcon sx={{ color: 'white' }} />
              <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                {message}
              </Typography>
            </MotionBanner>
          )}
        </AnimatePresence>

        {showReconnected && (
          <AnimatePresence mode="wait">
            {showReconnectedBanner && (
              <MotionReconnected
                $isDark={isDark}
                $position={position}
                initial={{ ...slideProps, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ ...slideProps, opacity: 0 }}
                transition={motionPresets.fadeInUp.transition}
              >
                <CloudOffIcon sx={{ color: 'white', transform: 'scaleX(-1)' }} />
                <Typography variant="body2" sx={{ color: 'white', fontWeight: 500 }}>
                  Back online
                </Typography>
              </MotionReconnected>
            )}
          </AnimatePresence>
        )}
      </>
    );
  },
);

OfflineIndicator.displayName = 'OfflineIndicator';

export default OfflineIndicator;
